import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SubsectionPreviewComponent } from '../../../../project-subsection/preview/subsection-preview.component';
import { SubsectionService } from '../../../services/subsection.service';
import { TopMediaInfoService } from '../../top-media-info/top-media-info.service';
import { CompareOption } from '../../../interfaces/subsection.interface';

@Component({
  selector: 'viewer-compare-button',
  templateUrl: './viewer-compare-button.component.html',
  styleUrl: './viewer-compare-button.component.scss',
  standalone: false,
})
export class ViewerCompareButtonComponent implements OnInit, OnDestroy {
  @Input() active: boolean = false;
  @Input() isFullscreen: boolean = false;
  @Input() compareOption: CompareOption | null = null;
  @Input() projectId: string | null = null;
  @Input() sectionId: string | null = null;
  @Input() subsectionId: string | null = null;
  @Input() optionId: string | null = null;
  @Input() versionNumber: number | null = null;
  @Input() fileId: string | null = null;
  @Output() clicked = new EventEmitter<void>();
  @Output() compareOptionSelected = new EventEmitter<CompareOption>();

  readonly CompareOption = CompareOption;
  isCompareMode = false;
  /** Resolved compare option: from uiState.compareViewMode or @Input() compareOption (for icon). */
  currentCompareOption: CompareOption | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly topMediaInfoService = inject(TopMediaInfoService);
  private readonly subsectionService = inject(SubsectionService);
  private readonly router = inject(Router);

  get isActive(): boolean {
    return this.active || this.isCompareMode;
  }

  get subSectionPreview(): SubsectionPreviewComponent | null {
    return this.topMediaInfoService.getSubsectionPreview();
  }

  ngOnInit(): void {
    this.subsectionService.uiState$.pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.isCompareMode = state?.compareMode ?? false;
      this.currentCompareOption = state?.compareViewMode ?? this.compareOption ?? null;
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectOption(option: CompareOption): void {
    this.compareOptionSelected.emit(option);
    if (option === CompareOption.ABOVE_AND_BELOW || option === CompareOption.SIDE_BY_SIDE) {
      this.subsectionService.updateUIState({ compareViewMode: option });
      if (this.subSectionPreview) {
        this.subSectionPreview.showComparison(option);
      } else {
        this.navigateToCompare(option);
      }
    }
    if (option === CompareOption.QUIT) {
      if (this.subSectionPreview) {
        this.subSectionPreview.quitComparison();
      } else {
        this.navigateToPreview();
      }
    }
    this.clicked.emit();
  }

  private navigateToCompare(viewMode: CompareOption): void {
    const state = this.subsectionService.subsectionState;
    const projectId = this.projectId ?? state?.section?.projectId ?? null;
    const sectionId = this.sectionId ?? state?.section?.id ?? null;
    const subsectionId = this.subsectionId ?? state?.subsection?.id ?? null;
    const optionId = this.optionId ?? state?.activeOption?.optionId ?? null;
    const versionNumber = this.versionNumber ?? state?.activeVersion?.versionNumber ?? null;
    const fileId = this.fileId ?? state?.activeFile?.id ?? null;

    if (!projectId || !sectionId || !subsectionId || !optionId || versionNumber == null || !fileId) {
      return;
    }

    this.router.navigate([`/projects/${projectId}/compare`], {
      queryParams: {
        projectId,
        sectionId,
        subsectionId,
        optionId,
        version: versionNumber,
        file: fileId,
        viewMode,
      },
    });
  }

  private navigateToPreview(): void {
    const state = this.subsectionService.subsectionState;
    const projectId = this.projectId ?? state?.section?.projectId ?? null;
    const sectionId = this.sectionId ?? state?.section?.id ?? null;
    const subsectionId = this.subsectionId ?? state?.subsection?.id ?? null;
    const optionId = this.optionId ?? state?.activeOption?.optionId ?? null;
    const versionNumber = this.versionNumber ?? state?.activeVersion?.versionNumber ?? null;
    const fileId = this.fileId ?? state?.activeFile?.id ?? null;

    if (!projectId || !sectionId || !subsectionId) {
      return;
    }

    this.subsectionService.navigateSubsection({
      projectId,
      sectionId,
      subsectionId,
      optionId: optionId ?? undefined,
      topicId: state?.activeTopic?.topicId ?? undefined,
      versionNumber: versionNumber ?? undefined,
      fileId: fileId ?? undefined,
    });
  }
}
